import { inject, Injectable } from '@angular/core';
import { PushNotificationChannel, PushNotificationChannelsKeys } from '@ag-common-lib/lib';
import { AgentKeys, ChangeSourceType } from '@ag-common-lib/public-api';
import { AgentService, ChangeSourceTypeService, PushNotificationsChannelsService } from 'ag-common-svc/public-api';
import { AgentChangesService, AgentSearchService } from '../../../../services/agent-search';
import { BehaviorSubject, filter, firstValueFrom, from, map, mergeMap, Observable, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

@Injectable()
export class NotificationChannelsService {
  private changeSourceTypeService = inject(ChangeSourceTypeService);
  private agentSearchService = inject(AgentSearchService);
  private agentChangesService = inject(AgentChangesService);

  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  private _agentDbId$ = new BehaviorSubject<string>(null);
  selectedNotificationsChannels$ = this._agentDbId$.pipe(
    filter(Boolean),
    // switchMap, not mergeMap: selecting a second agent has to abandon the first fetch
    // rather than race it, or a slow response for the previous agent can land last and
    // show that agent's channels against the one now on screen.
    switchMap(agentDbId =>
      // The channels come from the projection; the change signal is what replaces the
      // Firestore listener this used to hold, so a write made here still reflects back.
      this.agentChangesService.agentChanged$(agentDbId).pipe(
        startWith(undefined),
        switchMap(() => from(this.agentSearchService.getById(agentDbId))),
      ),
    ),
    map(agent => new Set(agent?.[AgentKeys.notificationChannels] ?? [])),
    shareReplay(1),
  );

  private channels$ = this.pushNotificationsChannelsService.getList().pipe(shareReplay(1));

  channelsMap$ = this.channels$.pipe(
    map(channels => {
      const map = new Map();
      channels?.forEach(channel => {
        const channelDbId = channel?.[BaseModelKeys.dbId];
        map.set(channelDbId, channel);
      });
      return map;
    }),
  );

  childrenChannelsMap$: Observable<Map<string, PushNotificationChannel[]>> = this.channels$.pipe(
    map(channels => {
      const map = new Map();
      channels?.forEach(channel => {
        const parentChannelDbId = channel?.[PushNotificationChannelsKeys.parentChannelDbId] ?? 'ROOT';

        if (!map?.has(parentChannelDbId)) {
          map.set(parentChannelDbId, [channel]);
          return;
        }
        map.get(parentChannelDbId).push(channel);
      });

      return map;
    }),
    shareReplay(1),
  );

  constructor(
    protected agentService: AgentService,
    private pushNotificationsChannelsService: PushNotificationsChannelsService,
  ) {}

  setAgentDbId(agentDbId: string) {
    this._agentDbId$.next(agentDbId);
  }

  addSubscriptionToChannel = async (addedChannel: PushNotificationChannel) => {
    const agentDbId = this._agentDbId$.value;
    const selectedNotificationsChannels = await firstValueFrom(this.selectedNotificationsChannels$);
    const channelsMap = await firstValueFrom(this.channelsMap$);
    const childrenChannelsMap = await firstValueFrom(this.childrenChannelsMap$);
    const addChannel = (channel: PushNotificationChannel) => {
      const channelDbId = channel?.[BaseModelKeys.dbId];
      selectedNotificationsChannels.add(channelDbId);
      const childChannels = childrenChannelsMap.get(channelDbId) ?? [];

      childChannels?.forEach(addChannel);
    };

    addChannel(addedChannel);

    const addParentSubscriptions = (channel: PushNotificationChannel) => {
      const parentChannelDbId = channel?.[PushNotificationChannelsKeys.parentChannelDbId];

      if (!parentChannelDbId) {
        return;
      }
      selectedNotificationsChannels.add(parentChannelDbId);

      const parentChannel = channelsMap.get(parentChannelDbId);
      addParentSubscriptions(parentChannel);
    };

    addParentSubscriptions(addedChannel);

    this.agentService.updateAgentFields(agentDbId, {
      [AgentKeys.notificationChannels]: Array.from(selectedNotificationsChannels.values()),
    }, this._sourceType);
  };

  removeSubscriptionToChannel = async (channel: PushNotificationChannel) => {
    const agentDbId = this._agentDbId$.value;
    const selectedNotificationsChannels = await firstValueFrom(this.selectedNotificationsChannels$);
    const channelsMap = await firstValueFrom(this.channelsMap$);
    const childrenChannelsMap = await firstValueFrom(this.childrenChannelsMap$);

    const removeChannel = (channel: PushNotificationChannel) => {
      const channelDbId = channel?.[BaseModelKeys.dbId];

      selectedNotificationsChannels.delete(channelDbId);
      const childChannels = childrenChannelsMap.get(channelDbId) ?? [];

      childChannels?.forEach(removeChannel);

      const checkParentSubscriptions = channel => {
        const parentChannelDbId = channel?.[PushNotificationChannelsKeys.parentChannelDbId];

        if (!parentChannelDbId) {
          return;
        }

        const sameLevelChannels = childrenChannelsMap.get(parentChannelDbId);
        if (!sameLevelChannels || !sameLevelChannels?.length) {
          return;
        }

        const hasSubscription = sameLevelChannels.some(channel =>
          selectedNotificationsChannels.has(channel?.[BaseModelKeys.dbId]),
        );

        if (hasSubscription) {
          return;
        }

        selectedNotificationsChannels.delete(parentChannelDbId);

        const parentChannel = channelsMap.get(parentChannelDbId);
        checkParentSubscriptions(parentChannel);
      };

      checkParentSubscriptions(channel);
    };

    removeChannel(channel);

    this.agentService.updateAgentFields(agentDbId, {
      [AgentKeys.notificationChannels]: Array.from(selectedNotificationsChannels.values()),
    }, this._sourceType);
  };
}
